import { useEffect, useRef, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { downloadSessionNotesPdf, useSessionNotes, useUpdateSessionNotes, useSessionToolsStream } from "@/lib/sessionToolsQueries";

/**
 * A single shared, collaborative text scratchpad — added to a Session's
 * Prep plan as resource_type="tool" (resource_id "notes_pad"). Both
 * coach and client can type; last-write-wins (no per-block locking like
 * WorkDocument blocks — this is a live conversational scratchpad, not a
 * durable structured record). To avoid an incoming update from the
 * other person clobbering a half-typed word, an update received within
 * LOCAL_PRIORITY_MS of this user's own last keystroke is deferred — but
 * (critically) it is RE-APPLIED once that window passes rather than
 * dropped forever, otherwise two people typing at the same moment would
 * each keep their own local text and never converge, silently losing
 * whichever edit the server didn't keep.
 */
const DEBOUNCE_MS = 600;
const LOCAL_PRIORITY_MS = 1500;

export default function SharedNotesPadTool({ workspaceId, sesId }) {
  useSessionToolsStream({ workspaceId, sesId, enabled: true });
  const { data } = useSessionNotes(workspaceId, sesId);
  const update = useUpdateSessionNotes(workspaceId, sesId);
  const [text, setText] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const lastLocalEditAtRef = useRef(0);
  const debounceRef = useRef(null);
  const savedFlashRef = useRef(null);
  const reapplyRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const latestServerContentRef = useRef("");

  useEffect(() => {
    if (data == null) return;
    latestServerContentRef.current = data.content || "";
    const remainingMs = LOCAL_PRIORITY_MS - (Date.now() - lastLocalEditAtRef.current);
    if (reapplyRef.current) {
      clearTimeout(reapplyRef.current);
      reapplyRef.current = null;
    }
    if (!hasInitializedRef.current || remainingMs <= 0) {
      setText(latestServerContentRef.current);
      hasInitializedRef.current = true;
    } else {
      // Still inside the local-edit priority window — don't clobber
      // active typing now, but re-check once the window actually
      // elapses so this (or a later, ref-read-at-fire-time) server
      // snapshot always lands, never a stale closed-over one.
      reapplyRef.current = setTimeout(() => {
        setText(latestServerContentRef.current);
      }, remainingMs + 50);
    }
  }, [data]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (reapplyRef.current) clearTimeout(reapplyRef.current);
    if (savedFlashRef.current) clearTimeout(savedFlashRef.current);
  }, []);

  function handleChange(e) {
    const value = e.target.value;
    setText(value);
    lastLocalEditAtRef.current = Date.now();
    // The user is actively typing again — cancel any re-apply that was
    // queued against an older snapshot; the next `data` change will
    // reschedule it against the up-to-date priority window.
    if (reapplyRef.current) {
      clearTimeout(reapplyRef.current);
      reapplyRef.current = null;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      update.mutate(value, {
        onSuccess: () => {
          setJustSaved(true);
          if (savedFlashRef.current) clearTimeout(savedFlashRef.current);
          savedFlashRef.current = setTimeout(() => setJustSaved(false), 1500);
        },
      });
    }, DEBOUNCE_MS);
  }

  const statusLabel = update.isPending ? "Saving…" : justSaved ? "Saved" : "Synced live";

  async function handleDownload() {
    setDownloading(true);
    try {
      await downloadSessionNotesPdf(workspaceId, sesId);
    } catch (err) {
      toast.error(err.message || "Couldn't download the notes PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="py-3" data-testid="shared-notes-pad-tool">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs text-ink/60" data-testid="shared-notes-pad-status">{statusLabel} between you and your coach/client</p>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-1.5 text-xs font-medium text-ink/75 hover:text-ink disabled:opacity-50 shrink-0"
          data-testid="shared-notes-pad-download-button"
        >
          {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download PDF
        </button>
      </div>
      <textarea
        value={text}
        onChange={handleChange}
        placeholder="Type notes here — both of you can see and edit this live..."
        className="w-full min-h-[240px] rounded-lg border border-ink/15 bg-white/70 p-3 text-sm text-ink resize-y focus:outline-none focus:ring-2 focus:ring-terracotta/40"
        data-testid="shared-notes-pad-textarea"
      />
    </div>
  );
}
